import { createReducer } from '@reduxjs/toolkit'
import { useAtomValue, useSetAtom } from 'jotai'
import { atomWithReducer } from 'jotai/utils'
import { createContext, useContext } from 'react'
import { Price, Token } from '@pancakeswap/swap-sdk-core'

import {
  Field,
  invertPrice,
  resetMintState,
  setFullRange,
  typeInput,
  typeLeftRangeInput,
  typeRightRangeInput,
  typeStartPriceInput,
} from './actions'

type FullRange = true

export interface MintState {
  readonly independentField: Field
  readonly typedValue: string | undefined
  readonly startPriceTypedValue: string // for the case when there's no liquidity
  readonly leftRangeTypedValue: Price<Token, Token> | undefined
  readonly rightRangeTypedValue: Price<Token, Token> | undefined
  readonly inverted: boolean
  readonly fullRange: boolean
}

export const initialState: MintState = {
  independentField: Field.CURRENCY_A,
  typedValue: '',
  startPriceTypedValue: '',
  leftRangeTypedValue: undefined,
  rightRangeTypedValue: undefined,
  inverted: false,
  fullRange: false
}

const reducer = createReducer<MintState>(initialState, (builder) =>
  builder
    .addCase(resetMintState, () => initialState)
    .addCase(setFullRange, (state) => {
      return {
        ...state,
        fullRange: true,
      }
    })
    .addCase(typeStartPriceInput, (state, { payload: { typedValue } }) => {
      return {
        ...state,
        startPriceTypedValue: typedValue,
      }
    })
    .addCase(typeLeftRangeInput, (state, { payload: { typedValue } }) => {
      const [valueLeft, valueRight] = [typedValue, state.rightRangeTypedValue ?? typedValue]
      const [leftRangeTypedValue, rightRangeTypedValue] = valueRight?.lessThan(valueLeft) ? [valueRight, valueLeft] : [valueLeft, valueRight]
      return {
        ...state,
        fullRange: false,
        leftRangeTypedValue,
        rightRangeTypedValue,
      }
    })
    .addCase(typeRightRangeInput, (state, { payload: { typedValue } }) => {
      const [valueLeft, valueRight] = [state.leftRangeTypedValue ?? typedValue, typedValue]
      const [leftRangeTypedValue, rightRangeTypedValue] = valueRight?.lessThan(valueLeft) ? [valueRight, valueLeft] : [valueLeft, valueRight]
      return {
        ...state,
        fullRange: false,
        leftRangeTypedValue,
        rightRangeTypedValue,
      }
    })
    .addCase(typeInput, (state, { payload: { field, typedValue, noLiquidity } }) => {
      if (noLiquidity) {
        // they're typing into the field they've last typed in
        if (field === state.independentField) {
          return {
            ...state,
            independentField: field,
            typedValue,
          }
        }
        // they're typing into a new field, store the other value

        return {
          ...state,
          independentField: field,
          typedValue,
        }
      }
      return {
        ...state,
        independentField: field,
        typedValue,
      }
    })
    .addCase(invertPrice, (state, { payload: { inverted } }) => {
      if (state.startPriceTypedValue!=="" && Number(state.startPriceTypedValue) > 0 && state.inverted !== inverted)
        return {
          ...state,
          inverted,
          startPriceTypedValue: (1 / Number(state.startPriceTypedValue)).toString(),
        }
      return {
        ...state,
        inverted
      }
    })
)

export const createFormAtom = () => atomWithReducer(initialState, reducer)

const LiquidityAtomContext = createContext({
  formAtom: createFormAtom(),
  onAddLiquidityCallback: (_hash: `0x${string}`) => {
    //
  },
})

export function useV3FormAddLiquidityCallback() {
  const ctx = useContext(LiquidityAtomContext)
  return ctx.onAddLiquidityCallback
}

export const LiquidityAtomProvider = LiquidityAtomContext.Provider

export function useV3FormState() {
  const ctx = useContext(LiquidityAtomContext)
  return useAtomValue(ctx.formAtom)
}

export function useV3FormDispatch() {
  const ctx = useContext(LiquidityAtomContext)
  return useSetAtom(ctx.formAtom)
}
